<template>
  <el-dialog title="下载图表" :visible.sync="visible" width="1080px" :before-close="() => (visible = false)">
    <div
      v-if="isEchart"
      class="chart-download"
      :style="{ minHeight: '574px' }"
      ref="chartContent"
      v-loading="chartLoading"
    >
      <img class="chart-img" v-show="chartImg" :src="chartImg" />
    </div>
    <div class="chart-download" v-else>
      <component
        :ref="researchModel.type"
        :is="researchModel.type"
        :rows="researchModel.rows"
        :statistics="researchModel.statistics"
        :chart-title="researchModel.title"
        customLegend
        by-download
        :byDashboard="researchModel.type === 'tableAnalysisChart'"
        :chartHeight="540"
      />
    </div>
    <span slot="footer">
      <el-button @click="visible = false">取 消</el-button>
      <el-button type="primary" :loading="loading" @click="downloadChart">下 载</el-button>
    </span>
  </el-dialog>
</template>

<script>
import pieChart from '@/components/chartComponents/pieChart';
import pieChart2 from '@/components/chartComponents/pieChart2';
import barChart from '@/components/chartComponents/barChart';
import barXChart from '@/components/chartComponents/barXChart';
import lineChart from '@/components/chartComponents/lineChart';
import lineChart2 from '@/components/chartComponents/lineChart2';
import numChart from '@/components/chartComponents/numChart';
import tableChart from '@/components/chartComponents/tableChart';
import tableAnalysisChart from '@/components/chartComponents/tableAnalysisChart';

import { checkChartInfo, exportExcel } from '@/lib/utils';
import Xlsx from 'xlsx';
import { services } from '@/service';

export default {
  components: {
    pieChart,
    pieChart2,
    barChart,
    barXChart,
    lineChart,
    lineChart2,
    numChart,
    tableChart,
    tableAnalysisChart
  },
  data() {
    return {
      loading: false,
      dataSet: null,
      chartLoading: false,
      chartImg: ''
    };
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    courseDetail: {
      type: Object,
      required: true
    },
    researchModel: {
      type: Object,
      required: true
    }
  },
  computed: {
    visible: {
      get() {
        return this.show;
      },
      set(val) {
        this.$emit('update:show', val);
      }
    },
    isEchart() {
      return ['lineChart', 'lineChart2', 'barChart', 'barXChart', 'pieChart', 'pieChart2'].includes(
        this.researchModel.type
      );
    }
  },
  async mounted() {
    if (this.isEchart) {
      this.initChartImg();
    } else {
      this.$nextTick(async () => {
        const el = this.$refs[this.researchModel.type];
        const { dataSet } = await el.refresh(
          checkChartInfo({
            colors: this.researchModel.colors,
            chartConfig: this.researchModel.chartConfig,
            rows: this.researchModel.rows,
            columns: this.researchModel.columns,
            statistics: this.researchModel.statistics,
            researchCondition: this.researchModel.conditionTemp,
            courseCondition: this.courseDetail._source.conditionTemp,
            courseId: this.courseDetail._id,
            originData: JSON.parse(this.researchModel.dataSet || '{}'),
            sortStatistic: JSON.parse(this.researchModel.sortStatistic || '{}'),
            relationFilterVal: this.researchModel.relationFilterVal,
            relationFilterName: this.researchModel.relationFilterName
          }),
          { haveOriginData: true }
        );
        this.dataSet = dataSet;
      });
    }
  },
  methods: {
    async initChartImg() {
      try {
        this.chartLoading = true;
        const result = await services.courseware.downloadChartImg(this.courseDetail._id, this.researchModel.index_id);
        const reader = new FileReader();
        reader.readAsDataURL(result);
        reader.onload = e => {
          this.chartImg = e.target.result;
          this.chartLoading = false;
        };
        reader.onerror = err => {
          console.error(err);
          this.chartLoading = false;
        };
      } catch (err) {
        this.$error(err);
      }
    },
    async downloadChart() {
      if (this.chartLoading) {
        return;
      }
      try {
        this.loading = true;
        if (['tableChart', 'tableAnalysisChart'].includes(this.researchModel.type)) {
          await this.excelExportHandle();
        } else {
          await this.saveCanvasHandle();
        }
        services.logger.handleLog({ operateName: '下载图表' });
        this.$message.success('下载成功');
        this.visible = false;
      } catch (err) {
        this.$error(err);
      } finally {
        this.loading = false;
      }
    },
    async saveCanvasHandle() {
      const a = document.createElement('a');
      a.target = '_blank';
      a.href = this.chartImg;
      a.setAttribute('download', `${this.researchModel.title}的图表分析`);
      a.click();
    },
    async excelExportHandle() {
      let sheet = null;
      const isTableAnalysisChart = this.researchModel.type === 'tableAnalysisChart';
      const excelData = [];
      let rows = [];
      if (isTableAnalysisChart) {
        rows = [{ isConst: true, columnKey: 'const', title: '常数' }, ...this.researchModel.rows];
        const analysisData = this.dataSet.analysis_data;

        if (!analysisData) {
          throw new Error('暂无数据，无法导出');
        }

        excelData.push(
          [
            '',
            '非标准化系数',
            '非标准化系数',
            '标准化系数',
            '标准化系数占比',
            'T',
            'Sig',
            'VIF',
            'DW',
            'R方',
            'F',
            'Sig'
          ],
          ['', 'B', '标注误差', '标准化系数', '标准化系数占比', 'T', 'Sig', 'VIF', 'DW', 'R方', 'F', 'Sig']
        );
        rows.forEach(rowItem => {
          const dataKey = rowItem.isConst ? 'const' : rowItem.title;
          excelData.push([
            rowItem.title,
            analysisData.beta_coefficient[dataKey],
            analysisData.standard_error[dataKey],
            analysisData.beta_standard[dataKey],
            analysisData.beta_probability[dataKey],
            analysisData.t_test[dataKey],
            analysisData.t_sig[dataKey],
            analysisData.vif[dataKey],
            analysisData.dw_value,
            analysisData.r_value,
            analysisData.f_value,
            analysisData.f_sig
          ]);
        });
        sheet = Xlsx.utils.aoa_to_sheet(excelData);
        // 列头合并
        const mergesInfo = [];
        let isMerge = false;
        for (let i = 0; i < excelData[0].length; i++) {
          if (i === 1 || i === 2) {
            if (!isMerge) {
              mergesInfo.push({
                s: { r: 0, c: 1 },
                e: { r: 0, c: 2 }
              });
              isMerge = true;
            }
          } else {
            mergesInfo.push({
              s: { r: 0, c: i },
              e: { r: 1, c: i }
            });
          }
        }
        // 数据行后4列合并
        for (let i = 0; i < 4; i++) {
          const colNum = i + 8;
          mergesInfo.push({
            s: { r: 2, c: colNum },
            e: { r: 2 + rows.length - 1, c: colNum }
          });
        }
        sheet['!merges'] = mergesInfo;
      } else {
        const { rowsData, columnsData } = this.dataSet;
        const haveRowName = rowsData.some(item => Object.prototype.hasOwnProperty.bind(item)('name'));
        excelData.push(columnsData);
        if (haveRowName) {
          excelData[0].unshift('/');
        }
        rowsData.forEach(rowDataItem => {
          const data = columnsData.map(columnKey => rowDataItem[columnKey] || 0);
          excelData.push(data);
        });
        sheet = Xlsx.utils.aoa_to_sheet(excelData);
      }
      if (!sheet) {
        return;
      }
      exportExcel(sheet, `${this.researchModel.title}.xlsx`);
    }
  }
};
</script>

<style lang="scss" scoped>
.chart-download {
  box-sizing: border-box;
  width: 100%;
  padding: 20px 20px 0;
  .chart-img {
    width: 100%;
  }
}
</style>
